/**
 * Phase 12+ EXP system. Cartridge-accurate Gen-3 formula with one
 * deliberate v0.1 simplification: every species uses the Medium-Fast
 * growth curve (L³). Real Pokémon has 6 curves (Erratic, Fast, Medium-
 * Fast, Medium-Slow, Slow, Fluctuating) — the differences are
 * balance-tier polish, not loop-tier, and v0.1's species roster doesn't
 * lean hard on any single curve. Future expansion: add `growthRate` per
 * species + a `expForLevel(level, growthRate)` switch.
 */

/**
 * Total EXP needed to BE level L on the Medium-Fast curve.
 *   level 1 = 1, level 5 = 125, level 10 = 1000, level 14 = 2744
 * "Total" is cumulative — a Pokémon with 200 exp is between L5 (125)
 * and L6 (216), needing 16 more to ding.
 */
export function expForLevel(level: number): number {
  if (level <= 1) return 0;
  return level * level * level;
}

/**
 * Highest level a Pokémon with `exp` total points has earned.
 *   exp=125 → L5 exactly
 *   exp=200 → L5 (still 16 short of L6)
 *   exp=216 → L6
 * Loops up from the current level rather than computing inverse to
 * avoid floating-point cube-root issues at exact boundaries (200^(1/3)
 * = 5.848 but we want 5).
 */
export function levelForExp(exp: number, fromLevel: number = 1): number {
  let level = Math.max(1, fromLevel);
  while (level < 100 && expForLevel(level + 1) <= exp) {
    level++;
  }
  return level;
}

/**
 * Cartridge Gen-3 EXP-gain formula (single participant, no Lucky Egg,
 * no traded multiplier — v0.1 doesn't have those):
 *
 *   exp = floor(baseExp * defeatedLevel * trainerMult / 7)
 *
 * Trainer multiplier = 1.5 for trainer-defeated; 1.0 for wild. The /7
 * divisor is a cartridge constant — without it, base-65 Charmander at
 * L5 wild would give ~46 EXP per kill (huge); with /7 it's ~46/7 = 6,
 * and L14 Onix in a trainer fight gives floor(77*14*1.5/7) = 231.
 *
 * Sanity-checked against cartridge:
 *   wild Pidgey L3 (base 55):       floor(55*3/7)         = 23 exp
 *   trainer Geodude L12 (base 73):  floor(73*12*1.5/7)    = 187 exp
 *   trainer Onix L14 (base 77):     floor(77*14*1.5/7)    = 231 exp
 *
 * The cartridge value is then scaled by RUN_EXP_MULTIPLIER.
 */

/**
 * Run-scale pacing knob. The cartridge curve assumes a 40-hour campaign;
 * this is a ~1-hour run game, and at 1× being Brock-ready demands ~30
 * trash fights. Applied after the cartridge floor so the formula above
 * stays verifiable against cartridge tables.
 *
 * 3× per first-friend playtest 2026-08-02 ("3x exp for testing phase")
 * — revisit against the second-friend run before the MVP gate closes.
 */
export const RUN_EXP_MULTIPLIER = 3;

export function computeExpGain(
  defeatedBaseExp: number,
  defeatedLevel: number,
  isTrainerBattle: boolean,
): number {
  const mult = isTrainerBattle ? 1.5 : 1;
  return Math.floor((defeatedBaseExp * defeatedLevel * mult) / 7) * RUN_EXP_MULTIPLIER;
}
